import { useCallback, useMemo, useRef } from 'react';
import ReactPlayer from 'react-player';

// 先送り判定の許容誤差(秒)。onProgress の間隔(1秒)+再生位置のゆらぎ分。
const FORWARD_TOLERANCE_SECONDS = 2;

export interface ProgressState {
  playedSeconds: number;
}

// 初回視聴(未完了)中のシーク(先送り)を禁止するガード。
// これまでに再生が到達した最大位置(maxPlayedSeconds)を追跡し、それより先へ
// ジャンプしたら最大位置まで引き戻す。巻き戻しと、視聴済み範囲内への移動は自由。
// 視聴完了後(2回目以降の視聴)は setEnabled(false) で無効化する。
export const useSeekGuard = () => {
  const playerRef = useRef<ReactPlayer>(null);
  const enabledRef = useRef(false);
  const maxPlayedSecondsRef = useRef(0);
  // 続きから再生をこれから行うか(開くごとに1回だけ)
  const pendingResumeRef = useRef(false);

  // 視聴完了時(onEnded 後)にガードを解除するために使う
  const setEnabled = useCallback((enabled: boolean) => {
    enabledRef.current = enabled;
  }, []);

  // 動画を開くたびに、サーバに記録済みの最大再生位置とガードの有効/無効
  // (未完了なら有効)をまとめて設定する。続きから再生の予約もここで決まる。
  const reset = useCallback((initialMaxPlayedSeconds: number, enabled: boolean) => {
    maxPlayedSecondsRef.current = Math.max(0, initialMaxPlayedSeconds);
    enabledRef.current = enabled;
    pendingResumeRef.current = enabled && maxPlayedSecondsRef.current > 0;
  }, []);

  // 未完了(ガード有効)の動画は、前回の到達位置から続きで再生を再開する。
  // 完了済み(ガード無効)は通常どおり先頭から。
  // HTML5 の canplay はシークのたびに再発火し onReady が複数回呼ばれ得るため、
  // 必ず「開くごとに1回だけ」実行する(毎回シークすると同じ位置に引き戻され
  // 続けて再生が進まなくなる)。
  const handleReady = useCallback(() => {
    if (pendingResumeRef.current) {
      pendingResumeRef.current = false;
      playerRef.current?.seekTo(maxPlayedSecondsRef.current, 'seconds');
    }
  }, []);

  const handleProgress = useCallback((state: ProgressState) => {
    const played = state.playedSeconds;
    if (
      enabledRef.current &&
      played > maxPlayedSecondsRef.current + FORWARD_TOLERANCE_SECONDS
    ) {
      // 未視聴区間への先送りを検知したので、視聴済みの最終位置へ引き戻す
      playerRef.current?.seekTo(maxPlayedSecondsRef.current, 'seconds');
      return;
    }
    maxPlayedSecondsRef.current = Math.max(maxPlayedSecondsRef.current, played);
  }, []);

  // heartbeat に載せる現在の再生位置(秒)。プレイヤー未マウント時は null。
  const getPlayedSeconds = useCallback((): number | null => {
    const current = playerRef.current?.getCurrentTime();
    return typeof current === 'number' && !Number.isNaN(current) ? current : null;
  }, []);

  // 戻り値を安定させる(消費側が useEffect の依存に使っても毎レンダー発火しない)
  return useMemo(
    () => ({ playerRef, setEnabled, reset, handleReady, handleProgress, getPlayedSeconds }),
    [setEnabled, reset, handleReady, handleProgress, getPlayedSeconds]
  );
};
